import * as React from 'react';
import { v4 as uuidv4 } from 'uuid';
import { CategoryRow } from './CategoryRow';
import { SalesCategory, MenuCategory } from '../types';
import { isNewMenuCategory } from '../utils';
import { getIdForCategory } from './utils';

interface MenuCategoryEditorProps {
  categories: MenuCategory[];
  saleCategories: SalesCategory[];
  onChange(categories: MenuCategory[]);
  hasTriedSubmitting: boolean;
}

const isSameCategory = (c1: MenuCategory, c2: MenuCategory): boolean => {
  if (isNewMenuCategory(c1) && isNewMenuCategory(c2)) {
    return c1.tempId === c2.tempId;
  } else if (!isNewMenuCategory(c1) && !isNewMenuCategory(c2)) {
    return c1.id === c2.id;
  }

  return false;
};

export const MenuCategoryEditor: React.FC<MenuCategoryEditorProps> = ({
  hasTriedSubmitting,
  saleCategories,
  categories,
  onChange,
}) => {
  const onChangeName = React.useCallback(
    (category: MenuCategory) => {
      const index = categories.findIndex((cat) => {
        return isSameCategory(cat, category);
      });

      const newList = [...categories];
      newList[index] = category;
      onChange(newList);
    },
    [categories, onChange]
  );

  const onClickAdd = React.useCallback(() => {
    const newCategory: MenuCategory = {
      tempId: uuidv4(),
      name: '',
      salesCategoryId: saleCategories[0].id,
    };

    onChange(categories.concat(newCategory));
  }, [categories, onChange, saleCategories]);

  const onMoveCategory = React.useCallback(
    (dragIndex, hoverIndex) => {
      const dragCard = categories[dragIndex];

      const itemsCopy = [...categories];
      itemsCopy.splice(dragIndex, 1);
      itemsCopy.splice(hoverIndex, 0, dragCard);

      onChange(itemsCopy);
    },
    [categories, onChange]
  );

  const onRemove = React.useCallback(
    (category: MenuCategory) => {
      const newCategories = categories.filter((cat) => {
        return !isSameCategory(cat, category);
      });

      onChange(newCategories);
    },
    [categories, onChange]
  );

  return (
    <div>
      {categories.map((category, index) => (
        <CategoryRow
          key={getIdForCategory(category)}
          category={category}
          onChange={onChangeName}
          index={index}
          moveCard={onMoveCategory}
          onRemove={onRemove}
          saleCategories={saleCategories}
          hasTriedSubmitting={hasTriedSubmitting}
        />
      ))}
      <a href='#' className='mt-3' onClick={onClickAdd}>
        <span className='pe-2'>+</span>
        <span>Add category</span>
      </a>
    </div>
  );
};
